import apiClient from '../config/api';
import type { Parameter, ParameterListResponse, ParameterHistoryEntry, TestFormulaResult } from '../types';

export interface ListParametersParams {
  type?: string;
  active?: string;
  search?: string;
  page?: number;
  limit?: number;
}

function mapParameter(data: Record<string, unknown>): Parameter {
  return {
    parameterId: data.parameter_id as number,
    parameterName: data.parameter_name as string,
    parameterType: data.parameter_type as 'INPUT' | 'CALCULATED',
    dataType: data.data_type as Parameter['dataType'],
    sourceType: data.source_type as Parameter['sourceType'],
    naturalLanguageFormula: data.natural_language_formula as string | undefined,
    executableFormula: data.executable_formula as string | undefined,
    validationRules: data.validation_rules as Parameter['validationRules'],
    dropdownValues: data.dropdown_values as string[] | undefined,
    displayOrder: data.display_order as number,
    isActive: data.is_active as boolean,
    createdBy: data.created_by as string,
    modifiedBy: data.modified_by as string,
    createdAt: data.created_at as string,
    modifiedAt: data.modified_at as string,
  };
}

export async function listParameters(params: ListParametersParams = {}): Promise<ParameterListResponse> {
  const query: Record<string, string> = {};
  if (params.type) query.type = params.type;
  if (params.active) query.active = params.active;
  if (params.search) query.search = params.search;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const response = await apiClient.get('/api/parameters', { params: query });
  const data = response.data;

  return {
    parameters: (data.parameters || []).map(mapParameter),
    total: data.total,
    page: data.page,
    limit: data.limit,
  };
}

export async function getParameter(parameterId: number): Promise<Parameter> {
  const response = await apiClient.get(`/api/parameters/${parameterId}`);
  return mapParameter(response.data.parameter);
}

export async function createParameter(body: {
  parameterName: string;
  parameterType: 'INPUT' | 'CALCULATED';
  dataType?: string;
  sourceType?: string;
  naturalLanguageFormula?: string;
  validationRules?: Record<string, unknown>;
  dropdownValues?: string[];
  displayOrder: number;
}): Promise<Parameter> {
  const response = await apiClient.post('/api/parameters', body);
  return mapParameter(response.data.parameter);
}

export async function updateParameter(
  parameterId: number,
  updates: {
    parameterName?: string;
    dataType?: string;
    sourceType?: string;
    naturalLanguageFormula?: string;
    validationRules?: Record<string, unknown>;
    dropdownValues?: string[];
    displayOrder?: number;
    isActive?: boolean;
  }
): Promise<Parameter> {
  const response = await apiClient.put(`/api/parameters/${parameterId}`, updates);
  return mapParameter(response.data.parameter);
}

export async function deleteParameter(parameterId: number): Promise<void> {
  await apiClient.delete(`/api/parameters/${parameterId}`);
}

export async function getParameterHistory(parameterId: number): Promise<ParameterHistoryEntry[]> {
  const response = await apiClient.get(`/api/parameters/${parameterId}/history`);
  return (response.data.history || []).map((entry: Record<string, unknown>) => ({
    historyId: entry.history_id,
    parameterId: entry.parameter_id,
    parameterName: entry.parameter_name,
    parameterType: entry.parameter_type,
    dataType: entry.data_type,
    naturalLanguageFormula: entry.natural_language_formula,
    executableFormula: entry.executable_formula,
    validationRules: entry.validation_rules,
    dropdownValues: entry.dropdown_values,
    displayOrder: entry.display_order,
    isActive: entry.is_active,
    changeType: entry.change_type,
    changedBy: entry.changed_by,
    changedAt: entry.changed_at,
    previousValues: entry.previous_values,
  }));
}

export async function testFormula(
  naturalLanguageFormula: string,
  sampleValues: Record<string, unknown>
): Promise<TestFormulaResult> {
  const response = await apiClient.post('/api/parameters/test-formula', {
    naturalLanguageFormula,
    sampleValues,
  });
  return {
    success: response.data.success,
    result: response.data.result,
    error: response.data.error,
    executableFormula: response.data.executable_formula ?? response.data.executableFormula,
  };
}
